import { Box, Flex, Group, Text } from '@mantine/core';
import { IconButton } from 'src/design-system/IconButton';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Client } from 'src/admin/users/types';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useCreateFamily } from '../hooks/useCreateFamily';
import CreateClientModal from '../CreateClientModalV2';
import {
  getAdvisorUserId,
  payloadFromInProgressFamilyV2,
  V2MemberRelationship,
} from './dataHelpers';
import MemberSearchList from './MemberSearchList';
import { SearchbarActionButton } from 'src/design-system/SearchbarActionButton';
import NameAndRelationshipsStep from './NameAndRelationshipsStep';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { CreateFamilySidebar } from './CreateFamilySidebar';

export default function CreateFamilyPage() {
  const navigate = useNavigate();
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { createFamily, isLoading } = useCreateFamily();
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Client[]>([]);
  const [showAddIndividual, setShowAddIndividual] = useState(false);

  const toggleMember = (client: Client) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === client.id);
      if (isSelected) return prev.filter(m => m.id !== client.id);
      return [...prev, client];
    });
  };

  const handleNewClientAdded = (client: Client) => {
    setSelectedMembers(prev => [...prev, client]);
    setShowAddIndividual(false);
  };

  const handleSubmit = async (
    familyName: string,
    relationships: Record<string, V2MemberRelationship>,
  ) => {
    const advisorUserId = getAdvisorUserId(
      selectedMembers,
      currentUser,
      isSystemAdmin,
      isServiceAdmin,
    );

    try {
      await createFamily(
        payloadFromInProgressFamilyV2(
          { selectedMembers, relationships, name: familyName },
          advisorUserId,
        ),
      );
      Mixpanel.track(MixpanelEvents.FAMILY_CREATED);
      navigate('/clients/families');
    } catch (error) {
      const message = ApiError.isApiError(ApiErrorCode.CLIENT_ALREADY_IN_FAMILY, error)
        ? error.message
        : 'An error occurred while creating the family. Please try again.';
      notifyAndLogError('Failed to create family', message, error);
    }
  };

  if (step === 'details') {
    return (
      <>
        <NameAndRelationshipsStep
          selectedMembers={selectedMembers}
          onBack={() => setStep('select')}
          onCancel={() => navigate('/clients/families')}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
        <SidebarContent>
          <SiestaBarLayout>
            <CreateFamilySidebar step="details" />
          </SiestaBarLayout>
        </SidebarContent>
        <CreateClientModal
          opened={showAddIndividual}
          close={() => setShowAddIndividual(false)}
          navigateAfterCreation={false}
          onClientCreated={handleNewClientAdded}
        />
      </>
    );
  }

  return (
    <>
      <Flex direction="column" h="100%">
        <FlowLayout
          header={
            <Box px="xl" pt="xxl" pb="sm">
              <Group justify="space-between" pb="lg">
                <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                  Select family members
                </Text>
                <DesignButton variant="text" onClick={() => navigate('/clients/families')}>
                  Cancel
                </DesignButton>
              </Group>

              <Group pb="md" gap="sm">
                <IconButton
                  size={48}
                  bg="brand.0"
                  c="brand.7"
                  style={{ flexShrink: 0 }}
                  onClick={() => {
                    if (searchOpen) setSearchTerm('');
                    setSearchOpen(o => !o);
                  }}
                >
                  {searchOpen ? <IconX size={24} /> : <IconSearch size={24} />}
                </IconButton>

                <SearchbarActionButton
                  searchOpen={searchOpen}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, email, address"
                  buttonLabel="Add new individual"
                  onButtonClick={() => setShowAddIndividual(true)}
                />
              </Group>
            </Box>
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <FlowNavigationFooter
                  onBack={() => navigate(-1)}
                  onAction={() => setStep('details')}
                  actionDisabled={selectedMembers.length < 2}
                />
              }
            >
              <Box px="xs">
                <MemberSearchList
                  selectedMembers={selectedMembers}
                  onToggle={toggleMember}
                  searchTerm={searchTerm}
                  noScroll
                  excludeInFamilies
                />
              </Box>
            </FlowBody>
          }
        />
      </Flex>
      <SidebarContent>
        <SiestaBarLayout>
          <CreateFamilySidebar step="select" />
        </SiestaBarLayout>
      </SidebarContent>
      <CreateClientModal
        opened={showAddIndividual}
        close={() => setShowAddIndividual(false)}
        navigateAfterCreation={false}
        onClientCreated={handleNewClientAdded}
      />
    </>
  );
}
